import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import {
    createFieldDate,
    createFieldDatetime,
    createFieldInput,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTemplateBuilder,
    createFieldTextArea
} from "../../../../components/input/template-builder/template-builder.logic";
import { createColumnDate, createColumnInput, createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { conditionFormChecker, putField } from "../../../../utils/form";
import { has } from "../../../../utils/menu-context";
import { callDepartment } from "../../master-mgt/department-responsible/department-responsible.logic";

const CardBorder = defineAsyncComponent(() => import("../../../../components/card/CardBorder"));
const TemplateBuilder = defineAsyncComponent(() => import("../../../../components/input/template-builder/TemplateBuilder"));
const SamplingMonitorProductList = defineAsyncComponent(() => import("./SamplingMonitorProductList"));
const AttachmentList = defineAsyncComponent(() => import("../../attachment/AttachmentList"));
const SamplingProductList = defineAsyncComponent(() => import("../../sampling/sampling-product/SamplingProductList"));

export const callSamplingMonitorType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/samplingMonitorType/cacheFindAll", { value: searchQuery });
export const callDepartmentFisherOther = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/department/cacheFindAll", {
        searchQuery,
        where: [{ name: "refDepartmentType.key", type: "String", operator: "=", value: "FO" }]
    });
export const callDepartmentSamplingDepartment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/department/cacheFindAll", {
        searchQuery,
        where: [{ name: "refDepartmentType.key", type: "String", operator: "in", value: ["FIQC", "FIRC"] }]
    });
export const callDepartmentRandomSamplingDepartment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/department/cacheFindAll", {
        searchQuery,
        where: [{ name: "refDepartmentType.key", type: "String", operator: "=", value: "RD" }]
    });
export const callSamplingCondition = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_CONDITION", value: searchQuery })
        .then(samplingConditions => samplingConditions.filter(d => ["M"].includes(d.key)));
export const callSamplingRequestStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_REQUEST_STATUS", value: searchQuery });
export const callSamplingRequestStatusForReceive = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_REQUEST_STATUS", value: searchQuery })
        .then(samplingRequestStatuses => samplingRequestStatuses.filter(d => ["Approve", "Reject"].includes(d.key)));
export const callSamplingProductStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_PRODUCT_STATUS", value: searchQuery });

export const mapSamplingStatusToMonitorStatus = ({ key }) => {
    return i18n.global.t(`model.samplingRequest.labels.status.${key}`);
};

export const createColumns = ({ props }) => {
    const { columnTemplate } = props;
    let defaultCompanyDepartment = null;
    if (!has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" })) {
        defaultCompanyDepartment = store.state.resource.auth.info?.user?.refDepartment?.refParent ? store.state.resource.auth.info?.user?.refDepartment?.refParent: store.state.resource.auth.info?.user?.refDepartment;
    }
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.samplingRequest.requestNo"), dataIndex: "requestNo", align: "center" }),
        createColumnDate({ title: () => i18n.global.t("model.samplingRequest.requestDate"), dataIndex: "requestDate" }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingRequest.refSamplingMonitorType"),
            dataIndex: "refSamplingMonitorType.name",
            customRender: ({ record }) => <div>{record.refSamplingMonitorType?.name}</div>,
            inputProp: () => ({ list: callSamplingMonitorType, optLabel: "name" }),
            search: {
                name: "refSamplingMonitorType.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingRequest.refRequesterDepartment"),
            dataIndex: "refRequesterDepartment.nameTh",
            // customRender: ({ record }) => <div>{record.refRequesterDepartment?.nameTh}</div>,
            inputProp: () => ({ list: callDepartmentFisherOther, optLabel: "nameTh" }),
            search: {
                name: "refRequesterDepartment.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingRequest.refSamplingMonitorDepartment"),
            dataIndex: "refSamplingDepartment.nameTh",
            inputProp: () => ({ list: callDepartment, optLabel: "nameTh" }),
            search: {
                visible: () => has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" }),
                name: "refSamplingDepartment.id",
                field: { searchValue: defaultCompanyDepartment }
            },
            width: 120
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingRequest.refSamplingStatus"),
            dataIndex: "refSamplingStatus.value",
            customRender: ({ record }) => <div>{mapSamplingStatusToMonitorStatus({ key: record?.refSamplingStatus?.key })}</div>,
            inputProp: () => ({
                list: ({ searchQuery }) =>
                    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", {
                        group: "SAMPLING_REQUEST_STATUS",
                        key: searchQuery,
                        value: searchQuery,
                        options: { force: true }
                    }),
                renderLabel: ({ item }) => <div>{mapSamplingStatusToMonitorStatus({ key: item?.key })}</div>
            }),
            search: {
                name: "refSamplingStatus.id"
            }
        })
    ];

    const template1 = ["requestNo", "requestDate", "refSamplingMonitorType.name", "refRequesterDepartment.nameTh", "refSamplingStatus.value"];
    if (columnTemplate === "template1") {
        return getColumnTemplate({ columns, template: template1 });
    }
    return columns;
};

const getColumnTemplate = ({ template, columns }) => {
    let filterColumns = columns.filter(d => template.includes(d.dataIndex)).map(d => ({ seq: template.indexOf(d.dataIndex), column: d }));
    return lodash.sortBy(filterColumns, "seq").map(data => data.column);
};

export const createField = ({ props, data, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "SamplingMonitor" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.samplingMonitor.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "SamplingMonitor" } })
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldSamplingMonitor({ props, data, key, isLoading })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "SamplingMonitorProduct" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.samplingMonitor.samplingMonitorProduct.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "SamplingMonitorProduct" } })
                    );
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: "samplingProducts",
                            fetchCriteria: data.samplingProduct?.fetchCriteria,
                            visibleSamplingRequest: false
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "SamplingMonitorProduct" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return () => (
                        <CardBorder {...cardProp}>
                            <SamplingMonitorProductList {...componentProp}></SamplingMonitorProductList>
                        </CardBorder>
                    );
                }
            },
            {
                key: `${prefix}samplingProducts`,
                label: () => i18n.global.t("views.samplingMonitor.samplingMonitorProduct.title"),
                changeLog: { getValueChildren: ({ record }) => record.samplingNameTh }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Attachment" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.samplingMonitor.attachment.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "Attachment" } })
                    );
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            parent: model,
                            group: "Attachment"
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "Attachment" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <AttachmentList {...componentProp}></AttachmentList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Receive" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.samplingMonitor.receive.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "Receive" } })
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldReceive({ props, data, key, isLoading })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "AnalysisResult" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("model.samplingRequest.labels.analysisResult")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "AnalysisResult" } })
                    );
                    const componentProp = lodash.mergeWith(
                        {
                            //menuContext,
                            menuContext: null,
                            viewOnly,
                            useForm: data.useForm,
                            parent: model,
                            visibleSearch: false,
                            isChildren: true,
                            isRepeat: props.isRepeat,
                            columnTemplate: "template6"
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "AnalysisResult" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <SamplingProductList {...componentProp}></SamplingProductList>
                        </CardBorder>
                    );
                }
            }
        ]
    });
};

export const createFieldSamplingMonitor = ({ props, data, key, isLoading }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({
                        data,
                        fields: [
                            createFieldInput({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}requestNo`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.samplingRequest.requestNo"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.requestNo,
                                            "onUpdate:value": value => (model.requestNo = value),
                                            disabled: viewOnly
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            }),
                            createFieldDate({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}requestDate`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.samplingRequest.requestDate"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.requestDate,
                                            "onUpdate:value": value => (model.requestDate = value),
                                            disabled: viewOnly
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            }),
                            createFieldSelect({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}refSamplingMonitorType`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.samplingRequest.refSamplingMonitorType"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.refSamplingMonitorType,
                                            "onUpdate:value": value => (model.refSamplingMonitorType = value),
                                            disabled: viewOnly,
                                            list: callSamplingMonitorType,
                                            optLabel: "name",
                                            renderLabel: ({ item }) => (
                                                <span>
                                                    {item.code} - {item.name}
                                                </span>
                                            )
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    ),
                                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.name`) }
                            })
                        ]
                    }),
                    skeleton: isLoading.value
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({
                        data,
                        fields: [
                            createFieldSelect({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}refRequesterDepartment`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.samplingRequest.refRequesterDepartment"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.refRequesterDepartment,
                                            "onUpdate:value": value => (model.refRequesterDepartment = value),
                                            disabled: viewOnly,
                                            list: callDepartmentFisherOther,
                                            optLabel: "nameTh"
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    ),
                                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
                            }),
                            createFieldSelect({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}refSamplingDepartment`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.samplingRequest.refSamplingMonitorDepartment"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.refSamplingDepartment,
                                            "onUpdate:value": value => (model.refSamplingDepartment = value),
                                            disabled: viewOnly,
                                            list: callDepartmentSamplingDepartment,
                                            optLabel: "nameTh"
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    ),
                                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
                            }),
                            createFieldTextArea({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}refRequesterDepartment.refAddr.addr`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.samplingRequest.requesterDepartmentAddr"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.refRequesterDepartment?.refAddr?.addr,
                                            disabled: true
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            }),
                            createFieldTextArea({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}requesterRemark`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.samplingRequest.requesterRemark"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.requesterRemark,
                                            "onUpdate:value": value => (model.requesterRemark = value),
                                            disabled: viewOnly
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            })
                        ]
                    }),
                    skeleton: isLoading.value
                })
            })
        ]
    });
};

export const createFieldReceive = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}considerDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.samplingRequest.considerDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.considerDatetime,
                            "onUpdate:value": value => (model.considerDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refConsiderBy.fullName`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.samplingRequest.refConsiderBy"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refConsiderBy?.fullName,
                            "onUpdate:value": value => (model.refConsiderBy.fullName = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refSamplingStatus`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.samplingRequest.refSamplingStatus"),
                inputProp: ({ field }) =>
                    lodash.mergeWith(
                        {
                            value: model.refSamplingStatus,
                            "onUpdate:value": value => (model.refSamplingStatus = value),
                            disabled: viewOnly,
                            list: callSamplingRequestStatusForReceive,
                            optLabel: "attribute.actionText"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } }),
                        (objValue, srcValue, key) => {
                            if (["value"].includes(key)) return srcValue || objValue;
                        }
                    ),
                changeLog: { getValue: ({ record }) => getStatusForTitle({ data: { useForm: { initialModel: record } } }) }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}officerRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.samplingRequest.officerRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.officerRemark,
                            "onUpdate:value": value => (model.officerRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const getStatusForTitle = ({ data }) => {
    const { initialModel } = data.useForm;
    if (initialModel.refSamplingStatus?.key === "Approve") {
        return initialModel.refSamplingStatus.attribute.actionText;
    }
    return initialModel.refSamplingStatus?.value;
};
